import { proxyCustomElement, HTMLElement, createEvent, h } from '@stencil/core/internal/client';
import { F as FormController } from './form.js';
import { O as Owner } from './Business.js';
import { i as identitySchema } from './business-identity-schema.js';
import { B as Button } from './button.js';
import { U as heading3 } from './parts.js';
import './details.js';
import { a as BusinessFormClickActions } from './event-types.js';
import { d as defineCustomElement$8 } from './custom-popper2.js';
import { d as defineCustomElement$7 } from './form-control-date2.js';
import { d as defineCustomElement$6 } from './form-control-number-masked2.js';
import { d as defineCustomElement$5 } from './form-control-select2.js';
import { d as defineCustomElement$4 } from './form-control-text2.js';
import { d as defineCustomElement$3 } from './form-control-tooltip2.js';
import { d as defineCustomElement$2 } from './identity-address-form.js';
import { d as defineCustomElement$1 } from './owner-form-inputs2.js';

const BusinessOwnerFormCore = /*@__PURE__*/ proxyCustomElement(class BusinessOwnerFormCore extends HTMLElement {
    loadingWatcher() {
        this.formLoading.emit(this.isLoading);
    }
    constructor() {
        super();
        this.__registerHost();
        this.clickEvent = createEvent(this, "click-event", 7);
        this.errorEvent = createEvent(this, "error-event", 7);
        this.formLoading = createEvent(this, "formLoading", 7);
        this.ownerSubmitted = createEvent(this, "ownerSubmitted", 7);
        this.getData = () => {
            this.isLoading = true;
            this.getOwner({
                onSuccess: (response) => {
                    this.instantiateOwner(response.data);
                },
                onError: ({ error, code, severity }) => {
                    this.errorEvent.emit({
                        message: error,
                        errorCode: code,
                        severity: severity
                    });
                },
                final: () => this.isLoading = false
            });
        };
        this.sendData = () => {
            this.isLoading = true;
            return new Promise((resolve) => {
                if (this.ownerId) {
                    this.patchOwner({
                        payload: this.payload,
                        onSuccess: (response) => {
                            this.ownerSubmitted.emit({ id: response.data.id });
                            this.instantiateOwner(response.data);
                            resolve(true);
                        },
                        onError: ({ error, code, severity }) => {
                            this.errorEvent.emit({
                                message: error,
                                errorCode: code,
                                severity: severity
                            });
                            resolve(false);
                        },
                        final: () => this.isLoading = false
                    });
                }
                else {
                    this.postOwner({
                        payload: this.payload,
                        onSuccess: (response) => {
                            this.ownerSubmitted.emit({ id: response.data.id });
                            this.instantiateOwner(response.data);
                            resolve(true);
                        },
                        onError: ({ error, code, severity }) => {
                            this.errorEvent.emit({
                                message: error,
                                errorCode: code,
                                severity: severity
                            });
                            resolve(false);
                        },
                        final: () => this.isLoading = false
                    });
                }
            });
        };
        this.instantiateOwner = async (data) => {
            this.owner = Object.assign({}, new Owner(data));
            await this.formController.setInitialValues(this.owner);
        };
        this.handleAddOwner = () => {
            const eventName = this.ownerId ? BusinessFormClickActions.updateOwner : BusinessFormClickActions.addOwner;
            this.clickEvent.emit({ name: eventName });
        };
        this.handleRemoveOwner = () => {
            this.removeOwner(this.ownerId);
            this.clickEvent.emit({ name: BusinessFormClickActions.removeOwner });
        };
        this.isLoading = false;
        this.formController = undefined;
        this.errors = {};
        this.owner = {};
        this.ownerId = undefined;
        this.businessId = undefined;
        this.getOwner = undefined;
        this.patchOwner = undefined;
        this.postOwner = undefined;
        this.allowOptionalFields = undefined;
        this.removeOwner = undefined;
        this.newFormOpen = undefined;
        this.ownersLength = undefined;
        this.validate = this.validate.bind(this);
        this.submit = this.submit.bind(this);
        this.validateAndSubmit = this.validateAndSubmit.bind(this);
    }
    async returnOwnerData() {
        return this.ownerId;
    }
    async validate() {
        return this.formController.validate();
    }
    async submit() {
        const isValid = await this.validate();
        if (isValid) {
            return this.sendData();
        }
    }
    async validateAndSubmit(event) {
        event.preventDefault();
        const isValid = await this.validate();
        if (isValid) {
            this.submit();
        }
    }
    get payload() {
        let formValues = new Owner(this.formController.values.getValue()).payload;
        if (this.ownerId) {
            return formValues;
        }
        else {
            return Object.assign(Object.assign({}, formValues), { business_id: this.businessId });
        }
    }
    get formTitle() {
        return this.ownerId ? 'Edit Business Owner' : 'Add Business Owner';
    }
    get submitButtonText() {
        return this.ownerId ? 'Update' : 'Add';
    }
    get showRemoveButton() {
        const blankForm = !this.ownerId && this.newFormOpen;
        if (this.ownersLength <= 1) {
            return false;
        }
        else {
            if (blankForm) {
                return true;
            }
            else if (this.ownerId && !this.newFormOpen) {
                return true;
            }
        }
    }
    componentWillLoad() {
        this.formController = new FormController(identitySchema('owner', this.allowOptionalFields));
        this.ownerId ? this.getData() : this.instantiateOwner({});
    }
    componentDidLoad() {
        this.formController.errors.subscribe(errors => (this.errors = Object.assign({}, errors)));
    }
    render() {
        return (h("form", { key: 'b7fc4c0d0fdf3a5aab0a246daffa59022255e6ae', onSubmit: this.validateAndSubmit }, h("fieldset", { key: 'eee56a5e2e26184086c1301576695b627a1f2ef4' }, h("legend", { key: 'b3f75638a502cca01da0ed54ec07660554dcd416', class: "fw-semibold fs-5", part: heading3 }, this.formTitle), h("br", { key: '0bc020cc7290615db3d3e1ec2b84d5af99ec86f0' }), h("div", { key: '2e7a09c9f4dffac21f2b3e1efe118542f18d89e8', class: "row gy-3" }, h("owner-form-inputs", { key: '78ffd296d4ecf0d5f2e50a06fb392cbefb1c9dc7', ownerDefaultValue: this.formController.getInitialValues(), errors: this.errors, formController: this.formController }), h("div", { key: '73256017645b6987cd96006e5fe080402b0602d2', class: "d-flex gap-2 justify-content-start" }, h(Button, { key: 'e2c288cf27ebc2d4dd9c29b2a2ed8e409d0c7564', variant: "secondary", type: "button", onClick: this.handleRemoveOwner, hidden: !this.showRemoveButton }, "Remove"), h(Button, { key: 'd3d604b4416c8196181ec4fd34b254a0909b2aff', variant: "primary", type: "submit", onClick: this.handleAddOwner, disabled: this.isLoading }, this.submitButtonText))), h("hr", { key: '4ad0047c67825990c1e4c02d377901d11a7937fa' }))));
    }
    static get watchers() { return {
        "isLoading": ["loadingWatcher"]
    }; }
}, [0, "owner-form-core", {
        "ownerId": [1, "owner-id"],
        "businessId": [1, "business-id"],
        "getOwner": [16],
        "patchOwner": [16],
        "postOwner": [16],
        "allowOptionalFields": [4, "allow-optional-fields"],
        "removeOwner": [16],
        "newFormOpen": [4, "new-form-open"],
        "ownersLength": [2, "owners-length"],
        "isLoading": [32],
        "formController": [32],
        "errors": [32],
        "owner": [32],
        "returnOwnerData": [64],
        "validate": [64],
        "submit": [64],
        "validateAndSubmit": [64]
    }, undefined, {
        "isLoading": ["loadingWatcher"]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["owner-form-core", "custom-popper", "form-control-date", "form-control-number-masked", "form-control-select", "form-control-text", "form-control-tooltip", "justifi-identity-address-form", "owner-form-inputs"];
    components.forEach(tagName => { switch (tagName) {
        case "owner-form-core":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, BusinessOwnerFormCore);
            }
            break;
        case "custom-popper":
            if (!customElements.get(tagName)) {
                defineCustomElement$8();
            }
            break;
        case "form-control-date":
            if (!customElements.get(tagName)) {
                defineCustomElement$7();
            }
            break;
        case "form-control-number-masked":
            if (!customElements.get(tagName)) {
                defineCustomElement$6();
            }
            break;
        case "form-control-select":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "form-control-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "form-control-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "justifi-identity-address-form":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
        case "owner-form-inputs":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}
defineCustomElement();

export { BusinessOwnerFormCore as B, defineCustomElement as d };
